import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  getTreeDepth,
  buildHeaderRows,
} from "../../utility/monthlyReportHeaders";

const STICKY_BG = "#fff";
const COMPANY_BAND_BG = "#f0f4fa";
const GROUP_HEADER_BG = "#f7f9fc";
const TOTALS_BG = "#e9dfc9";

const DATE_COL_WIDTH = 120;

// Sticky-left offset for Date column.
const dateStickySx = {
  position: "sticky",
  left: 0,
  zIndex: 3,
  minWidth: DATE_COL_WIDTH,
  maxWidth: DATE_COL_WIDTH,
  backgroundColor: STICKY_BG,
};

const getShortDay = (day) => {
  if (!day) return "-";

  const normalizedDay = String(day).trim().toLowerCase();

  const dayMap = {
    sunday: "Sun",
    monday: "Mon",
    tuesday: "Tue",
    wednesday: "Wed",
    thursday: "Thu",
    friday: "Fri",
    saturday: "Sat",
  };

  return dayMap[normalizedDay] || day.slice(0, 3);
};

export default function MonthlyReportTable({ report }) {
  const clients = report?.clients || [];
  const rows = report?.rows || [];
  const totals = report?.totals || {};

  const globalMaxDepth = useMemo(() => {
    if (clients.length === 0) return 1;

    return Math.max(
      ...clients.map((c) => getTreeDepth(c.columnTree))
    );
  }, [clients]);

  const totalHeaderRows = 1 + globalMaxDepth;

  const headerRowsByClient = useMemo(() => {
    const map = {};

    clients.forEach((client) => {
      map[client.clientId] = buildHeaderRows(
        client.columnTree,
        globalMaxDepth
      );
    });

    return map;
  }, [clients, globalMaxDepth]);

  if (clients.length === 0) {
    return (
      <Typography align="center" color="text.secondary" sx={{ mt: 4 }}>
        No companies match the selected filters.
      </Typography>
    );
  }

  return (
    <TableContainer
      sx={{
        overflow: "auto",
        border: "1px solid #eee",
        borderRadius: 2,
        maxWidth: "100%",
        pb: "18px",
      }}
    >
      <Table size="small" sx={{ borderCollapse: "separate" }}>
        <TableHead>

          <TableRow>
            <TableCell
              rowSpan={totalHeaderRows}
              sx={{
                ...dateStickySx,
                fontWeight: 700,
                fontSize: 12.5,
                zIndex: 4,
                textAlign: "center",
              }}
            >
              Date / Day
            </TableCell>

            {clients.map((client) => (
              <TableCell
                key={client.clientId}
                colSpan={client.leafColumns.length}
                align="center"
                sx={{
                  backgroundColor: COMPANY_BAND_BG,
                  fontWeight: 700,
                  fontSize: 13,
                  borderLeft: "2px solid #d7deea",
                }}
              >
                {client.clientName}
              </TableCell>
            ))}
          </TableRow>

          {/* Remaining header rows */}
          {Array.from({ length: globalMaxDepth }).map((_, rowIndex) => (
            <TableRow key={rowIndex}>
              {clients.map((client) => {
                const cells =
                  headerRowsByClient[client.clientId][rowIndex] || [];

                return cells.map((cell, idx) => (
                  <TableCell
                    key={cell.key}
                    colSpan={cell.colSpan}
                    rowSpan={cell.rowSpan}
                    align="center"
                    sx={{
                      backgroundColor: GROUP_HEADER_BG,
                      fontWeight: 600,
                      fontSize: 11.5,
                      borderLeft:
                        idx === 0
                          ? "2px solid #d7deea"
                          : undefined,
                    }}
                  >
                    {cell.label}
                  </TableCell>
                ));
              })}
            </TableRow>
          ))}
        </TableHead>                            

        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.date} hover>
              {/* Merged Date + Day column */}
              <TableCell
                sx={{
                  ...dateStickySx,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 1.3,
                  }}
                >
                  {new Date(row.date).toLocaleDateString("en-GB")}
                </Typography>

                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    lineHeight: 1.3,
                  }}
                >
                  {row.day}
                </Typography>
              </TableCell>

              {clients.map((client) =>
                client.leafColumns.map((col, idx) => {
                  const value =
                    row.values?.[client.clientId]?.[col.key];

                  const display =
                    value === null || value === undefined
                      ? "-"
                      : value;

                  const cell = (
                    <TableCell
                      key={`${client.clientId}-${col.key}`}
                      align="center"
                      sx={{
                        fontSize: 12.5,
                        maxWidth: 160,
                        borderLeft:
                          idx === 0
                            ? "2px solid #d7deea"
                            : undefined,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {display}
                    </TableCell>
                  );

                  return typeof value === "string" && value ? (
                    <Tooltip
                      key={`${client.clientId}-${col.key}`}
                      title={value}
                      arrow
                    >
                      {cell}
                    </Tooltip>
                  ) : (
                    cell
                  );
                })
              )}
            </TableRow>
          ))}

          {/* Total row */}
          <TableRow>
            <TableCell                                       
              sx={{
                ...dateStickySx,
                backgroundColor: TOTALS_BG,
                fontWeight: 700,
                fontSize: 12.5,
                textAlign: "center",
              }}
            >
              Total
            </TableCell>

            {clients.map((client) =>
              client.leafColumns.map((col, idx) => {
                const value =
                  totals?.[client.clientId]?.[col.key];

                return (
                  <TableCell 
                    key={`total-${client.clientId}-${col.key}`}
                    align="center"
                    sx={{
                      backgroundColor: TOTALS_BG,
                      fontWeight: 700,
                      fontSize: 12.5,
                      borderLeft:
                        idx === 0
                          ? "2px solid #d7deea"
                          : undefined,
                    }}
                  >
                    {value === null || value === undefined
                      ? 0
                      : value}
                  </TableCell>
                );
              })
            )}
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}
